import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Wallet, Calendar, TrendingUp, TrendingDown, Minus, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCashRegister() {
  const { user, getAuthHeaders } = useAuth();
  const [registers, setRegisters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    branch_id: 'all',
    date_from: '',
    date_to: '',
  });

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchRegisters();
  }, [filters]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, getAuthHeaders());
      let availableBranches = response.data;
      
      // Manager and Admin can only see their assigned branch
      if ((user?.role === 'manager' || user?.role === 'admin') && user?.branch_id) {
        availableBranches = response.data.filter(b => b.id === user.branch_id);
        setFilters(prev => ({ ...prev, branch_id: user.branch_id }));
      }
      
      setBranches(availableBranches);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchRegisters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.branch_id && filters.branch_id !== 'all') params.append('branch_id', filters.branch_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await axios.get(
        `${API}/cash-register/history?${params.toString()}`,
        getAuthHeaders()
      );
      setRegisters(response.data);
    } catch (error) {
      toast.error('Error al cargar historial de caja');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

  const getDifferenceIcon = (diff) => {
    if (diff > 0) return <TrendingUp className="w-4 h-4 text-blue-500" />;
    if (diff < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-emerald-500" />;
  };

  const getDifferenceColor = (diff) => {
    if (diff > 0) return 'text-blue-500';
    if (diff < 0) return 'text-red-500';
    return 'text-emerald-500';
  };

  // Calculate totals
  const totalCash = registers.reduce((sum, r) => sum + (r.total_cash_sales || 0) + (r.total_combined_cash || 0), 0);
  const totalCard = registers.reduce((sum, r) => sum + (r.total_card_sales || 0) + (r.total_combined_card || 0), 0);
  const totalTransfer = registers.reduce((sum, r) => sum + (r.total_transfer_sales || 0), 0);
  const totalDifference = registers.reduce((sum, r) => sum + (r.cash_difference || 0), 0);
  const totalTransactions = registers.reduce((sum, r) => sum + (r.transactions_count || 0), 0);

  return (
    <AdminLayout title="Historial de Cortes de Caja">
      <div className="space-y-6" data-testid="admin-cash-register">
        {/* Filters */}
        <Card className="bg-zinc-900 border-zinc-800 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Sucursal</Label>
              <Select
                value={filters.branch_id}
                onValueChange={(value) => setFilters({ ...filters, branch_id: value })}
                disabled={!isMasterAdmin && user?.branch_id}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Todas las sucursales" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {isMasterAdmin && (
                    <SelectItem value="all" className="text-amber-500">Todas</SelectItem>
                  )}
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id} className="text-white">
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Desde</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Hasta</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <p className="text-xs text-zinc-500 mb-1">Total Efectivo</p>
            <p className="text-xl font-bold text-emerald-500">{formatCurrency(totalCash)}</p>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <p className="text-xs text-zinc-500 mb-1">Total Tarjeta</p>
            <p className="text-xl font-bold text-blue-500">{formatCurrency(totalCard)}</p>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <p className="text-xs text-zinc-500 mb-1">Total Transferencia</p>
            <p className="text-xl font-bold text-purple-500">{formatCurrency(totalTransfer)}</p>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <p className="text-xs text-zinc-500 mb-1">Diferencia Total</p>
            <p className={`text-xl font-bold ${getDifferenceColor(totalDifference)}`}>
              {totalDifference > 0 ? '+' : ''}{formatCurrency(totalDifference)}
            </p>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 p-4">
            <p className="text-xs text-zinc-500 mb-1">Total Transacciones</p>
            <p className="text-xl font-bold text-gold">{totalTransactions}</p>
          </Card>
        </div>

        {/* Registers Table */}
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400">Sucursal</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400">Inicio</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400">Efectivo</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400">Tarjeta</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400">Esperado</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400">Contado</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400">Diferencia</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-zinc-400">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-zinc-500">
                      Cargando...
                    </td>
                  </tr>
                ) : registers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-zinc-500">
                      <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No hay cortes de caja registrados
                    </td>
                  </tr>
                ) : (
                  registers.map((register) => (
                    <tr key={register.id} className="hover:bg-zinc-800/50">
                      <td className="px-4 py-3 text-sm text-white">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-zinc-500" />
                          {register.date || new Date(register.opened_at).toLocaleDateString('es-MX')}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-zinc-500" />
                          {register.branch_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-zinc-400">
                        {formatCurrency(register.opening_amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-emerald-500">
                        {formatCurrency((register.total_cash_sales || 0) + (register.total_combined_cash || 0))}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-blue-500">
                        {formatCurrency((register.total_card_sales || 0) + (register.total_combined_card || 0))}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-zinc-300">
                        {register.status === 'closed' ? formatCurrency(register.expected_cash) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-zinc-300">
                        {register.status === 'closed' ? formatCurrency(register.closing_amount) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {register.status === 'closed' ? (
                          <div className={`flex items-center justify-end gap-1 ${getDifferenceColor(register.cash_difference)}`}>
                            {getDifferenceIcon(register.cash_difference)}
                            {register.cash_difference > 0 ? '+' : ''}
                            {formatCurrency(register.cash_difference)}
                          </div>
                        ) : (
                          <span className="text-zinc-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        {register.status === 'open' ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-500">
                            Abierta
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs rounded-full bg-zinc-700 text-zinc-400">
                            Cerrada
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}
